import { Menu } from "@headlessui/react"
import { MoreVertical, ChevronRight, Trash2 } from "lucide-react"

interface WorkTask {
  id: string
  task_name: string
  client_name: string | null
  company: string | null
  hourly_rate: number
  is_active: boolean
}

interface Props {
  job: WorkTask
  onSelect: (job: WorkTask) => void
  onDelete: (job: WorkTask) => void
  isActive: boolean
  disabled: boolean
}

export default function JobCard({ job, onSelect, onDelete, isActive, disabled }: Props) {
  return (
    <div
      className={`rounded-2xl border transition ${
        isActive
          ? "border-[#2563EB] bg-blue-50 dark:bg-blue-950/20 dark:border-[#2563EB]"
          : "bg-white dark:bg-neutral-900 border-gray-200 dark:border-gray-700"
      } ${disabled ? "opacity-50" : ""}`}
    >
      <div className="flex items-center gap-1 p-2">
        <button
          onClick={() => onSelect(job)}
          disabled={disabled}
          className="flex-1 flex items-center justify-between gap-3 px-3 py-3 text-left rounded-xl disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-neutral-800/60 active:bg-gray-100 dark:active:bg-neutral-800 transition"
        >
          <div className="min-w-0">
            <h3 className="text-base font-semibold truncate text-black dark:text-white">{job.task_name}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 truncate">
              ${job.hourly_rate.toFixed(2)}/hr{job.client_name ? ` · ${job.client_name}` : ""}
            </p>
            {isActive && (
              <p className="mt-1.5 text-xs font-medium text-[#2563EB] flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-[#2563EB] animate-pulse" />
                In progress
              </p>
            )}
          </div>
          <ChevronRight className="h-5 w-5 text-gray-400 shrink-0" />
        </button>

        <Menu as="div" className="relative shrink-0">
          <Menu.Button className="p-2.5 rounded-xl text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-neutral-800 transition">
            <MoreVertical className="h-4 w-4" />
          </Menu.Button>
          <Menu.Items className="absolute right-0 mt-1 w-48 origin-top-right rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-neutral-900 shadow-lg z-10 py-1 focus:outline-none">
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={() => onDelete(job)}
                  disabled={isActive}
                  className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-left text-red-600 dark:text-red-400 disabled:opacity-40 disabled:cursor-not-allowed ${active ? "bg-red-50 dark:bg-red-950/30" : ""}`}
                >
                  <Trash2 className="h-4 w-4" />
                  Delete job
                </button>
              )}
            </Menu.Item>
          </Menu.Items>
        </Menu>
      </div>
    </div>
  )
}
